import React, { useState, useMemo, useEffect, useCallback } from "react";

import { API_BASE_URL, endpoint } from "../../api/endpoint";

import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";

export const useMiscSettlement = () => {
  const dispatch = useDispatch();

  const { authData,storeId } = useSelector((state) => state.loginSlice);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search states
  const [selectedOption, setSelectedOption] = useState("settlement_id");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  // Filter states (including static and dynamic)
  const [filters, setFilters] = useState({
    settlement_created_at_from: "",
    settlement_created_at_to: "",
  });

  // Dynamic filter options fetched from the API
  const [filterOptions, setFilterOptions] = useState({
    buyerAppIds: [],
    sellerAppIds: [],
    settlementStatuses: [],
    providerStatuses: [],
    selfStatuses: [],
  });

  const searchOptions = [
    { value: "settlement_id", label: "Settlement ID" },
    { value: "txn_id", label: "Settement Txn. ID" },
    { value: "provider_id", label: "Provider ID" },
    { value: "provider_name", label: "Provider Name" },

  ];

  useEffect(() => {
    // Debounce the search input
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);


  const getSettlements = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${endpoint.GET_MISC_SETTLEMENTS}?`;
      if (debouncedSearchValue) {
        url += `&${selectedOption}=${debouncedSearchValue}`;
      }

      // Filter parameters
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          let value = filters[key];

          if (key === "settlement_created_at_to") {
            const toDate = new Date(value);
            toDate.setHours(23, 59, 59, 999);
            value = toDate.toISOString();
          }

          if (key === "settlement_created_at_from") {
            const fromDate = new Date(value);
            fromDate.setHours(0, 0, 0, 0);
            value = fromDate.toISOString();
          }

          url += `&${key}=${value}`;
        }
      });

      // Sorting parameters
      if (sortConfig.key) {
        url += `&sort_by=${sortConfig.key}&sort_order=${sortConfig.direction}`;
      }

      // Pagination parameters
      url += `&page=${currentPage}&limit=${itemsPerPage}`;

      const result = await apiHandler({
        url: url,
        method: "GET",

        authToken: authData,
        dispatch,
        storeId
      });

      

      if (result?.data?.data?.settlements) {
        setSettlements(result?.data?.data?.settlements);
        setTotalOrderCount(result.data.data.pagination?.count || 0);
      } else {
        setSettlements([]);
        setTotalOrderCount(0);
      }

      // Handle dynamic filters (if available)
      if (result?.data?.data?.filter) {
        setFilterOptions({
          buyerAppIds: result.data.data.filter.buyer_app_ids || [],
          sellerAppIds: result.data.data.filter.seller_app_ids || [],
          settlementStatuses: result.data.data.filter.settlement_status || [],
          providerStatuses: result.data.data.filter.provider_statuses || [],    
          selfStatuses: result.data.data.filter.self_statuses || [],
        });
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearchValue,
    selectedOption,
    filters,
    sortConfig,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    getSettlements();
  }, [getSettlements]);

  // --- Handlers for UI Interactions ---
  const handleDropdownChange = (e) => {
    const selectedFilter = e.target.value;
    setSelectedOption(selectedFilter);
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    setCurrentPage(1);
  };

  const allowedSortKeys = [
    "settlement_diff_amount",
    
  ];
  const handleSort = (key) => {
    if (!allowedSortKeys.includes(key)) return;
    setSortConfig((current) => {
      const newConfig = {
        key,
        direction:
          current.key === key && current.direction === "asc" ? "desc" : "asc",
      };
      return newConfig;
    });
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      settlement_created_at_from: "",
      settlement_created_at_to: "",
      settlement_status: "",
      buyer_app_id: "",
      seller_app_id: "",
      provider_status: "",
      self_status: "",
    });
    setSearchValue("");
    setSelectedOption("settlement_id");
    setSortConfig({ key: null, direction: "asc" });
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  // --- Computed Values & Utility Functions ---
  const totalPages = Math.ceil(totalOrderCount / itemsPerPage);
  const startItem =
    totalOrderCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalOrderCount);

  // These are redundant if API handles sorting and pagination directly
  const paginatedSettlements = useMemo(() => settlements, [settlements]);

  return {
    settlements,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedSettlements,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
    filterOptions, // Include dynamic filters
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    searchOptions,
  };
};
